/**
 * Owner: Masa Bayu
 * File: spsu-plate-duplicate-evidence.ts
 * Date:Time: 2026-10-20 19:34
 * Description: Plate↔teks hidup keputusan pendua · bukti kandungan + luas, paintTone bukan bukti (§438 R-2).
 */

import type { BBox, TextElement } from '../extract/spsu-types';

/** Di bawah ini tiada teks hidup yang menuntut kawasan plate · plate berdiri sendiri. */
export const PLATE_LIVE_CLAIM_COVERAGE = 0.3;
/** Plate dianggap diwakili teks hidup apabila ≥95% luasnya berada dalam kotak teks yang mencetak. */
export const PLATE_DUPLICATE_COVERAGE = 0.95;
/** Nisbah minimum perkataan plate yang mesti muncul dalam teks hidup untuk dianggap sepadan. */
export const PLATE_TOKEN_MATCH = 0.6;

export type PlateVerdictKind = 'duplicate' | 'uncertain' | 'unique';

export type PlateVerdictEvidence =
  /** Rentetan plate sepadan dengan kandungan teks hidup · bukti terkuat. */
  | 'ocr_content_match'
  /** Tiada rentetan plate · kotak teks hidup (lapisan teks sumber) meliputi plate. */
  | 'footprint_contained'
  /** Rentetan plate tidak diwakili teks hidup di kawasan itu · kandungan unik. */
  | 'content_conflict'
  /** 0.3–0.95 · geometri separa sahaja · tidak boleh diputuskan (§438 P3). */
  | 'partial_overlap'
  /** <0.3 · tiada teks hidup menuntut kawasan plate. */
  | 'no_live_claim';

export type PlateVerdict = {
  readonly kind: PlateVerdictKind;
  readonly coverage: number;
  readonly evidence: PlateVerdictEvidence;
  readonly matchedTextIds: readonly string[];
};

/** Teks yang benar-benar mencetak · Pass 1 menulis 'transparent' · teks itu tidak menuntut kawasan. */
function isPaintedText(text: TextElement): boolean {
  if (text.content.trim().length === 0) return false;
  return text.style.color.trim().toLowerCase() !== 'transparent';
}

type Clip = { readonly id: string; readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number };

function clipToPlate(plate: BBox, text: TextElement): Clip | null {
  const box = text.bbox;
  const x0 = Math.max(plate.x, box.x);
  const y0 = Math.max(plate.y, box.y);
  const x1 = Math.min(plate.x + plate.width, box.x + box.width);
  const y1 = Math.min(plate.y + plate.height, box.y + box.height);
  if (!(x1 > x0) || !(y1 > y0)) return null;
  return { id: text.id, x0, y0, x1, y1 };
}

/**
 * Luas plate yang dilitupi gabungan kotak teks hidup (luas kesatuan sejati,
 * bukan jumlah bertindih) · geometri tepat tanpa andaikan satu kotak sudah cukup.
 */
export function liveCoverageOfPlate(
  plate: BBox,
  liveTexts: readonly TextElement[],
): { readonly coverage: number; readonly textIds: readonly string[] } {
  const clips: Clip[] = [];
  for (const text of liveTexts) {
    if (!isPaintedText(text)) continue;
    const clip = clipToPlate(plate, text);
    if (clip) clips.push(clip);
  }
  const area = plate.width * plate.height;
  if (!(area > 0) || clips.length === 0) return { coverage: 0, textIds: [] };

  const edges = new Set<number>([plate.y, plate.y + plate.height]);
  for (const clip of clips) {
    edges.add(clip.y0);
    edges.add(clip.y1);
  }
  const ys = [...edges].sort((a, b) => a - b);

  let covered = 0;
  for (let i = 0; i < ys.length - 1; i += 1) {
    const y0 = ys[i]!;
    const y1 = ys[i + 1]!;
    const band = y1 - y0;
    if (!(band > 0)) continue;
    const spans = clips
      .filter((clip) => clip.y0 <= y0 + 1e-9 && clip.y1 >= y1 - 1e-9)
      .map((clip) => [clip.x0, clip.x1] as const)
      .sort((a, b) => a[0] - b[0]);
    let width = 0;
    let open: readonly [number, number] | null = null;
    for (const span of spans) {
      if (!open) {
        open = span;
      } else if (span[0] <= open[1]) {
        open = [open[0], Math.max(open[1], span[1])];
      } else {
        width += open[1] - open[0];
        open = span;
      }
    }
    if (open) width += open[1] - open[0];
    covered += width * band;
  }
  return { coverage: Math.min(1, covered / area), textIds: clips.map((clip) => clip.id) };
}

const PUNCT = /[.,:;!?"'`()\[\]{}·…–—\-/\\]+/g;

function normalizePhrase(content: string): string {
  return content
    .toLowerCase()
    .replace(PUNCT, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Rentetan plate diwakili teks hidup · padanan rentetan penuh atau ≥60% perkataan plate
 * ada dalam teks hidup. Tiada pembetulan bahasa · perbandingan sahaja.
 */
export function plateContentMatchesLive(plateContent: string, liveContent: string): boolean {
  const plate = normalizePhrase(plateContent);
  const live = normalizePhrase(liveContent);
  if (plate.length === 0 || live.length === 0) return false;
  if (live.includes(plate)) return true;
  const liveTokens = new Set(live.split(' '));
  const tokens = plate.split(' ');
  const hits = tokens.filter((token) => liveTokens.has(token)).length;
  return hits / tokens.length >= PLATE_TOKEN_MATCH;
}

/**
 * Keputusan tunggal plate · tidak membaca paintTone.
 * - kandungan plate ada (OCR plate itu sendiri) → HANYA padanan konten membuktikan pendua.
 *   Liputan penuh geometri dengan konten yang BERBEZA = konflik · bukan bukti pendua ·
 *   plate tidak boleh dibuang atas geometri semata-mata (§438 R-2).
 * - kandungan plate tiada (Pass 3 pra-OCR) → hanya liputan penuh dalam kotak teks
 *   sumber yang membuktikan pendua · separa => `uncertain` (kekalkan + NOT PROVEN).
 */
export function adjudicatePlate(input: {
  readonly plate: BBox;
  readonly liveTexts: readonly TextElement[];
  readonly plateContent?: string;
}): PlateVerdict {
  const { coverage, textIds } = liveCoverageOfPlate(input.plate, input.liveTexts);
  const content = (input.plateContent ?? '').trim();
  const contained = coverage >= PLATE_DUPLICATE_COVERAGE;
  const claimed = coverage >= PLATE_LIVE_CLAIM_COVERAGE;

  if (content.length > 0) {
    const byId = new Set(textIds);
    const matched = input.liveTexts
      .filter((text) => byId.has(text.id))
      .filter((text) => plateContentMatchesLive(content, text.content))
      .map((text) => text.id);
    if (matched.length > 0) {
      return { kind: 'duplicate', coverage, evidence: 'ocr_content_match', matchedTextIds: matched };
    }
    return { kind: 'unique', coverage, evidence: 'content_conflict', matchedTextIds: textIds };
  }

  if (contained) {
    return { kind: 'duplicate', coverage, evidence: 'footprint_contained', matchedTextIds: textIds };
  }
  if (!claimed) {
    return { kind: 'unique', coverage, evidence: 'no_live_claim', matchedTextIds: [] };
  }
  return { kind: 'uncertain', coverage, evidence: 'partial_overlap', matchedTextIds: textIds };
}

/** Ringkasan satu baris untuk saluran metrik sedia ada · tiada medan provenance baharu. */
export function describePlateVerdict(verdict: PlateVerdict): string {
  return `${verdict.kind}:${verdict.evidence}:${verdict.coverage.toFixed(2)}`;
}
